import React, { useState, useEffect } from 'react';
import { collection, addDoc, deleteDoc, doc, updateDoc, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import './CategoryManagement.css';
import './shared.css';
import './mobile.css';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const getAllCategories = async () => {
    const categoriesSnapshot = await getDocs(collection(db, 'categories'));
    return categoriesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  };

  const loadCategories = async () => {
    try {
      const data = await getAllCategories();
      setCategories(data);
    } catch (error) {
      setMessage('Error loading categories: ' + error.message);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    try {
      setLoading(true);
      await addDoc(collection(db, 'categories'), {
        name: newCategory.trim(),
        createdAt: new Date().toISOString()
      });
      setNewCategory('');
      setMessage('Category added successfully');
      loadCategories();
    } catch (error) {
      setMessage('Error adding category: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setEditValue(category.name);
  };

  const handleUpdate = async (categoryId) => {
    if (!editValue.trim()) return;

    try {
      setLoading(true);
      await updateDoc(doc(db, 'categories', categoryId), {
        name: editValue.trim(),
        updatedAt: new Date().toISOString()
      });
      setEditingCategory(null);
      setMessage('Category updated successfully');
      loadCategories();
    } catch (error) {
      setMessage('Error updating category: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setEditValue('');
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'categories', categoryToDelete.id));
      setMessage('Category deleted successfully');
      loadCategories();
    } catch (error) {
      setMessage('Error deleting category: ' + error.message);
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
    }
  };

  return (
    <div className="category-management">
      <h2>Category Management</h2>
      
      <div className="add-category-form">
        <input
          type="text"
          placeholder="Enter new category name..."
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
        />
        <button onClick={handleAddCategory} disabled={loading}>
          Add Category
        </button>
      </div>

      <div className="categories-list">
        {categories.map(category => (
          <div key={category.id} className="category-item">
            {editingCategory?.id === category.id ? (
              <>
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="edit-input"
                  autoFocus
                />
                <div className="category-actions">
                  <button 
                    className="update-btn"
                    onClick={() => handleUpdate(category.id)}
                    disabled={loading}
                  >
                    Update
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <span>{category.name}</span>
                <div className="category-actions">
                  <button onClick={() => handleEdit(category)}>Edit</button>
                  <button onClick={() => handleDeleteClick(category)}>Delete</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={`category "${categoryToDelete?.name}"`}
      />

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default CategoryManagement; 